<template>
  <div class="comment-input" :class="{ compact: compact }">
    <img :src="userAvatar" alt="Your avatar" class="input-avatar" />

    <div class="input-wrapper">
      <textarea
        ref="textarea"
        v-model="content"
        :placeholder="placeholder"
        class="comment-textarea"
        :rows="1"
        @input="autoResize"
        @keydown.enter.exact.prevent="submitComment"
        @keydown.esc="handleCancel"
      ></textarea>

      <div class="input-actions">
        <VoiceDictationButton v-model="content" label="" />
        <button
          class="emoji-btn"
          @click.stop="showEmojiPicker = !showEmojiPicker"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="#65676b">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>
          </svg>
        </button>

        <button
          v-if="content.trim()"
          class="send-btn"
          @click="submitComment"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="#1877f2">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
          </svg>
        </button>
      </div>

      <!-- Emoji Picker -->
      <div v-if="showEmojiPicker" class="emoji-picker-wrapper">
        <EmojiPicker
          :native="true"
          :hide-search="true"
          :hide-group-icons="true"
          @select="onEmojiSelect"
        />
      </div>
    </div>

    <!-- Cancel button for replies -->
    <button v-if="compact" class="cancel-btn" @click="handleCancel">
      Cancel
    </button>
  </div>
</template>

<script>
import { PostComments } from "@/services/team-posts-service";
import EmojiPicker from "vue3-emoji-picker";
import "vue3-emoji-picker/css";
import VoiceDictationButton from "@/components/VoiceDictationButton.vue";
import { avatarUrl } from "@/utils/avatar";

export default {
  name: "CommentInput",
  components: {
    EmojiPicker,
    VoiceDictationButton,
  },
  props: {
    userData: {
      type: Object,
      required: true,
    },
    postId: {
      type: [Number, String],
      required: true,
    },
    parentCommentId: {
      type: [Number, String],
      default: null,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    autoFocus: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["comment-submitted", "cancel"],
  data() {
    return {
      content: "",
      showEmojiPicker: false,
      isSubmitting: false,
    };
  },
  computed: {
    userAvatar() {
      if (this.userData.userImage) {
        return this.getImgUrl(this.userData.userImage);
      }
      return this.getImgUrl("default-image.png");
    },
    placeholder() {
      return this.parentCommentId ? "Write a reply..." : "Write a comment...";
    },
  },
  mounted() {
    if (this.autoFocus) {
      this.$nextTick(() => {
        this.$refs.textarea?.focus();
      });
    }

    // Close emoji picker when clicking outside
    document.addEventListener("click", this.closeEmojiPicker);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeEmojiPicker);
  },
  methods: {
    getImgUrl(imageName) {
      return avatarUrl(imageName);
    },

    autoResize() {
      const textarea = this.$refs.textarea;
      if (textarea) {
        textarea.style.height = "auto";
        textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px";
      }
    },

    closeEmojiPicker() {
      this.showEmojiPicker = false;
    },

    onEmojiSelect(emoji) {
      this.content += emoji.i;
      this.showEmojiPicker = false;
      this.$refs.textarea?.focus();
    },

    handleCancel() {
      this.content = "";
      this.$emit("cancel");
    },

    async submitComment() {
      if (!this.content.trim() || this.isSubmitting) return;

      this.isSubmitting = true;

      try {
        const response = await PostComments.createComment({
          postId: this.postId,
          parentCommentId: this.parentCommentId,
          userId: this.userData.id,
          userName: this.userData.name,
          userImage: this.userData.userImage,
          userType: this.userData.userType,
          content: this.content.trim(),
        });

        if (response.data.success) {
          this.$emit("comment-submitted", response.data.comment);
          this.content = "";

          // Reset textarea height
          this.$nextTick(() => {
            if (this.$refs.textarea) {
              this.$refs.textarea.style.height = "auto";
            }
          });
        }
      } catch (error) {
        console.error("Error creating comment:", error);
        alert("Failed to post comment. Please try again.");
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.comment-input {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;
}

.comment-input.compact {
  margin-top: 8px;
  margin-bottom: 0;
}

.input-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.compact .input-avatar {
  width: 28px;
  height: 28px;
}

.input-wrapper {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  background: #f0f2f5;
  border-radius: 20px;
  padding: 0 12px;
}

.comment-textarea {
  flex: 1;
  border: none;
  background: transparent;
  padding: 8px 0;
  font-size: 15px;
  resize: none;
  outline: none;
  max-height: 120px;
  line-height: 1.4;
}

.comment-textarea::placeholder {
  color: #65676b;
}

.input-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.emoji-btn,
.send-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.emoji-btn:hover,
.send-btn:hover {
  background: #e4e6eb;
}

.emoji-picker-wrapper {
  position: absolute;
  bottom: 100%;
  right: 0;
  z-index: 100;
  margin-bottom: 8px;
}

.cancel-btn {
  padding: 6px 12px;
  border: none;
  background: none;
  color: #65676b;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
}

.cancel-btn:hover {
  text-decoration: underline;
}

@media (max-width: 768px) {
  .emoji-picker-wrapper {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    margin-bottom: 0;
    display: flex;
    justify-content: center;
    background: rgba(0, 0, 0, 0.5);
    padding: 16px;
  }
}
</style>
